import { Stripe } from "@stripe/stripe-js";
import { createReducer } from "typesafe-actions";

import SubscriptionInterval from "@mapmycustomers/shared/enum/SubscriptionInterval";
import Plan from "@mapmycustomers/shared/types/Plan";
import CurrentPlan from "@mapmycustomers/shared/types/plans/CurrentPlan";
import Tier from "@mapmycustomers/shared/types/plans/Tier";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import { calculatePrices, PriceInfo } from "../util/price";
import { findTierByIntervalAndLicenses } from "../util/tier";

import {
  Actions,
  hideCheckoutModal,
  initialize,
  resetSuccessState,
  selectAddOn,
  setLicenses,
  setTier,
  showCheckoutModal,
  upgrade,
} from "./actions";

export interface BillingState {
  addOns: Plan[];
  annual: boolean;
  checkoutModalVisible: boolean;
  currentPlan: CurrentPlan | undefined;
  error: ApiError | undefined;
  licenses: number;
  loading: boolean;
  minimalTier: Tier | undefined;
  priceInfo: PriceInfo;
  selectedAddOn: Plan | undefined;
  selectedTier: Tier | undefined;
  showSuccessState: boolean;
  stripe: Stripe | undefined;
  tiers: Tier[];
  upgradeError: ApiError | undefined;
  upgradeLoading: boolean;
}

const initialState: BillingState = {
  addOns: [],
  annual: true,
  checkoutModalVisible: false,
  currentPlan: undefined,
  error: undefined,
  licenses: 0,
  loading: false,
  minimalTier: undefined,
  priceInfo: {
    adjustedPrice: { currency: "USD", value: 0 },
    dueTodayPrice: { currency: "USD", value: 0 },
    proratedPrice: { currency: "USD", value: 0 },
    subscriptionPrice: { currency: "USD", value: 0 },
    switchingToAnnual: false,
  },
  selectedAddOn: undefined,
  selectedTier: undefined,
  showSuccessState: false,
  stripe: undefined,
  tiers: [],
  upgradeError: undefined,
  upgradeLoading: false,
};

const billing = createReducer<BillingState, Actions>(initialState)
  .handleAction(initialize.request, (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleAction(
    initialize.success,
    (state, { payload: { addOns, currentPlan, licenses, stripe, tiers } }) => {
      const selectedTier =
        findTierByIntervalAndLicenses(tiers, SubscriptionInterval.ANNUAL, licenses) ||
        currentPlan.tier;
      return {
        ...state,
        addOns,
        annual: true,
        currentPlan,
        licenses,
        loading: false,
        minimalTier: selectedTier,
        priceInfo: calculatePrices(currentPlan, selectedTier, undefined, licenses),
        selectedTier,
        stripe,
        tiers,
      };
    }
  )
  .handleAction(initialize.failure, (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleAction(upgrade.request, (state) => ({
    ...state,
    upgradeError: undefined,
    upgradeLoading: true,
  }))
  .handleAction(upgrade.success, (state) => ({
    ...state,
    showSuccessState: true,
    upgradeLoading: false,
  }))
  .handleAction(upgrade.failure, (state, action) => ({
    ...state,
    upgradeError: action.payload,
    upgradeLoading: false,
  }))
  .handleAction(resetSuccessState, (state) => ({
    ...state,
    showSuccessState: false,
  }))
  .handleAction(setLicenses, (state, { payload }) => {
    const minimalTier =
      findTierByIntervalAndLicenses(state.tiers, SubscriptionInterval.ANNUAL, payload) ??
      state.minimalTier;

    // keep currently selected tier, if it's "higher" than the minimal suitable
    // for the given number of licenses
    let selectedTier = minimalTier;
    if (selectedTier!.upsellOrder < state.selectedTier!.upsellOrder) {
      selectedTier = state.selectedTier;
    }

    return {
      ...state,
      licenses: payload,
      minimalTier,
      priceInfo: calculatePrices(state.currentPlan!, selectedTier!, state.selectedAddOn, payload),
      selectedTier,
    };
  })
  .handleAction(setTier, (state, { payload }) => ({
    ...state,
    priceInfo: calculatePrices(state.currentPlan!, payload, state.selectedAddOn, state.licenses),
    selectedTier: payload,
  }))
  .handleAction(selectAddOn, (state, { payload }) => ({
    ...state,
    priceInfo: calculatePrices(state.currentPlan!, state.selectedTier!, payload, state.licenses),
    selectedAddOn: payload,
  }))
  .handleAction(showCheckoutModal, (state) => ({
    ...state,
    checkoutModalVisible: true,
  }))
  .handleAction(hideCheckoutModal, (state) => ({
    ...state,
    checkoutModalVisible: false,
  }));

export * from "./selectors";
export type BillingActions = Actions;
export default billing;
